import { useTheme } from '@mui/material/styles';
import React from 'react';

import type { ChatSettings } from '@models/chat';

import Ethora from '@utils/messenger/ethora';

import EthoraMessengerError from './EthoraMessengerError';

interface EthoraMessengerProps {
  settings: ChatSettings;
  token: string;
  chatRoomJid?: string;
  renderOneRoom?: boolean;
}

export default function EthoraMessenger({
  settings,
  token,
  chatRoomJid,
  renderOneRoom,
}: EthoraMessengerProps) {
  const { palette, typography } = useTheme();

  return (
    <Ethora.Chat
      roomJID={chatRoomJid}
      config={{
        xmppSettings: {
          devServer: settings.devServer,
          host: settings.baseUrl,
          conference: settings.conference,
          xmppPingOnSendEnabled: true,
        },
        baseUrl: settings.baseUrl,
        newArch: true,
        refreshTokens: { enabled: true },
        jwtLogin: {
          token,
          enabled: true,
        },
        setRoomJidInPath: false,
        /*
          colors: {
            primary: palette.primary.main,
            secondary: palette.secondary.main,
          },
        */
        disableChatInfo: {
          disableHeader: false,
          disableDescription: true, // Hides chat description
          disableType: true, // Hides chat type
          disableMembers: true, // Displays chat member list, but cannot click into member profile
          disableChatHeaderMenu: true, // Hides 'Report' and 'Leave' chat options
        },
        chatHeaderSettings: {
          hide: false,
          disableCreate: true, // Hides 'New chat room' btn
          disableMenu: true, // Hides 'Profile, Settings, Logout' section
          hideSearch: renderOneRoom, // Search bar for chats
        },
        clearStoreBeforeInit: true,
        disableNewChatButton: true,
        disableRoomConfig: true,
        disableProfilesInteractions: true,
        // Patients have 1 chat room with their practitioners
        disableRoomMenu: renderOneRoom,
        disableRooms: renderOneRoom,
        enableRoomsRetry: {
          enabled: true,
          helperText: 'Initializing room',
        },
        hiddenRooms: {
          titles: ['Main chat'],
        },
        fallbackScreens: {
          noConnection: <EthoraMessengerError type="connection" />,
          noRoom: <EthoraMessengerError type="room" />,
          noUser: <EthoraMessengerError type="user" />,
        },
        // Colour, Font size, and Font family
        colors: {
          primary: palette.primary.main,
          secondary: palette.primary.contrastText,
          icons: palette.primary.main,
          iconsBg: palette.common.accentLightblue,
          ownMessageBackground: palette.info.light,
          otherMessageBackground: palette.background.paper,
          inputBackground: palette.primary.contrastText,
        },
        backgroundChat: {
          color: palette.background.default,
        },
        typography: {
          fontFamily: typography.fontFamily,
          fontSize: typography.body1.fontSize,
        },
      }}
    />
  );
}
